// Landing page — Direction B desktop redesign (entry 41). Structure:
// SiteHeader (logo/nav/sign-in/language) -> hero band (violet tint,
// headline + one-line explanation + two actions) -> category tiles ->
// featured products grid -> SiteFooter. Header, hero, and the first
// product row must all be visible without scrolling at 1366x768 and
// 1920x1080 — verified with real screenshots, not just written to spec.
//
// Mobile (<768px) keeps the same content, single column throughout — no
// separate mobile-only markup, one responsive layout (see
// LandingPage.module.css's breakpoints). SiteHeader's own nav row hides
// below 768px on its own (components/SiteHeader.module.css); the hero's
// two actions plus SiteHeader's always-visible "Sign in" link cover mobile
// navigation without a hamburger menu.
//
// Hero actions: both "Send a request" and "Upload BOQ or PDF" route to
// /start — components/RequestLinesForm.jsx (the form both the guest and
// account-holder paths render) is the ONE merged form now, with a file drop
// zone above the always-available manual line rows (this task's own
// brief). The upload button additionally stashes a one-shot
// utils/pendingUploadFocus.js flag so that form focuses its drop zone once
// it actually mounts (which may be several guest-flow steps later — OTP,
// details — not immediately on this navigate() call).
//
// Category tiles: top-level categories (parent_id === null) from GET
// /public/catalog/categories, EV Accessories included same as any other —
// each links to /browse?category=<id> (pages/CatalogBrowsePage.jsx reads
// that as its initial drill-in path). Icons come from utils/categoryIcons.js
// — the same lookup ProductCard's own image-placeholder fallback uses, so a
// product card and its category tile always agree visually.
//
// Featured products: unchanged in substance from before this task (max 8,
// GET /public/catalog/featured, zero-featured is a normal state that just
// omits the section) — reflowed into a responsive grid instead of a
// single-column list. Cards render with ProductCard's `variant="featured"`
// (components/ProductCard.jsx): clicking a card opens /catalog/:id (pages/
// ProductDetailPage.jsx), "Request price" is hidden, and "Add to request"
// is the only action. A "View full catalog ->" link below the grid goes to
// /catalog (CatalogBrowsePage.jsx mounted at a second path — /browse stays
// the category-tile target, unchanged).
//
// Hero search box: a plain, uncontrolled-fetch text input + submit that
// navigates to /catalog?q=<term> on submit (empty/whitespace input is a
// no-op) — CatalogBrowsePage.jsx seeds its own search state from that `q`
// param on arrival.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import SiteHeader from '../components/SiteHeader.jsx';
import SiteFooter from '../components/SiteFooter.jsx';
import ProductCard from '../components/ProductCard.jsx';
import { useCatalogCategories, useFeaturedProducts } from '../hooks/usePublicCatalog.js';
import { setPendingLine } from '../utils/pendingLine.js';
import { buildLineFromProduct } from '../utils/productDisplay.js';
import { setPendingUploadFocus } from '../utils/pendingUploadFocus.js';
import {
  CATEGORY_ICONS,
  getCategoryIconForId,
  getCategoryNameForId,
} from '../utils/categoryIcons.js';
import styles from './LandingPage.module.css';

export default function LandingPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const featuredQuery = useFeaturedProducts();
  const categoriesQuery = useCatalogCategories();
  // Hero search box — a plain GET navigation to /catalog?q=..., not a
  // controlled catalog fetch of its own (CatalogBrowsePage.jsx owns that,
  // seeded from this same `q` param on arrival). Empty/whitespace-only input
  // does nothing rather than navigating to an unfiltered /catalog — the
  // buyer already has "View full catalog" below for that.
  const [heroSearch, setHeroSearch] = useState('');
  const handleHeroSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = heroSearch.trim();
    if (!trimmed) return;
    navigate(`/catalog?q=${encodeURIComponent(trimmed)}`);
  };

  const handleRequestPrice = (product) => {
    setPendingLine(buildLineFromProduct(product, lang));
    navigate('/start');
  };

  // Entry Point 2 (catalog-picker task) — stays on this page, unlike
  // handleRequestPrice above.
  const handleAddToRequest = (product) => {
    setPendingLine(buildLineFromProduct(product, lang));
  };

  const featured = featuredQuery.data || [];
  const categories = categoriesQuery.data || [];
  const topLevelCategories = categories.filter((c) => c.parent_id === null);

  // The upload hero button stashes a one-shot flag (mirrors
  // utils/pendingLine.js's own "Request price" hand-off) so whichever
  // screen the guest flow eventually reaches the request form on can focus
  // the drop zone — the flag has to survive the OTP/details steps in
  // between, since /start doesn't render the form immediately for a new
  // guest (this task's own brief: "the upload one focuses the drop zone").
  const handleUploadClick = () => {
    setPendingUploadFocus();
    navigate('/start');
  };

  return (
    <div className={styles.page}>
      <SiteHeader />

      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <h1 className={styles.title}>{t('portal.landing.title')}</h1>
          <p className={styles.subtitle}>{t('portal.landing.subtitle')}</p>
          <div className={styles.heroActions}>
            <button
              type="button"
              className={`${styles.heroButton} ${styles.primary}`}
              onClick={() => navigate('/start')}
            >
              {t('portal.landing.sendRequest')}
            </button>
            <button
              type="button"
              className={`${styles.heroButton} ${styles.secondary}`}
              onClick={handleUploadClick}
            >
              {t('portal.landing.uploadBoq')}
            </button>
          </div>
          <form className={styles.heroSearchForm} onSubmit={handleHeroSearchSubmit}>
            <input
              type="search"
              className={styles.heroSearchInput}
              placeholder={t('portal.landing.searchPlaceholder')}
              value={heroSearch}
              onChange={(e) => setHeroSearch(e.target.value)}
              aria-label={t('portal.landing.searchPlaceholder')}
            />
            <button type="submit" className={styles.heroSearchButton}>
              <Search size={16} aria-hidden="true" />
              {t('portal.landing.searchSubmit')}
            </button>
          </form>
        </div>
      </div>

      <div className={styles.section}>
        {topLevelCategories.length > 0 && (
          <>
            <h2 className={styles.categoriesTitle}>{t('portal.landing.categoriesTitle')}</h2>
            <ul className={styles.categoryTiles}>
              {topLevelCategories.map((category) => {
                const Icon = CATEGORY_ICONS[category.name] || Package;
                const name = lang === 'am' && category.name_am ? category.name_am : category.name;
                return (
                  <li key={category.id}>
                    <button
                      type="button"
                      className={styles.categoryTile}
                      onClick={() => navigate(`/browse?category=${category.id}`)}
                    >
                      <Icon size={22} aria-hidden="true" />
                      <span>{name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {featured.length > 0 && (
          <>
            <hr className="hairline" />
            <h2 className={styles.sectionTitle}>{t('portal.landing.featuredTitle')}</h2>
            <p className={styles.sectionHint}>{t('portal.catalog.noPricesHint')}</p>
            <ul className={styles.productGrid}>
              {featured.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onRequestPrice={() => handleRequestPrice(product)}
                  onAddToRequest={() => handleAddToRequest(product)}
                  categoryIcon={getCategoryIconForId(categories, product.category_id)}
                  categoryName={getCategoryNameForId(categories, product.category_id, lang)}
                  dense
                  variant="featured"
                />
              ))}
            </ul>
            <Link to="/catalog" className={styles.viewFullCatalogLink}>
              {t('portal.landing.viewFullCatalog')}
            </Link>
          </>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
